import { Box } from '@wix/design-system';
import React, { useCallback, useMemo } from 'react';
import { useSharedAltTextScreenContext } from '../context/alt-text-screen-context';
import { getDisabledButtonTooltipKeySuffix, getSingleImageDisabledTooltipKeySuffix } from '../utils/get-disabled-button-tooltip-key';
import { imageNeedsAltText } from '../utils/get-form-status-and-status-message';
import { AltTextImageList } from './alt-text-image-list';
import { AltTextOverviewSection } from './alt-text-overview-section';
import { AltTextSummary } from './alt-text-summary';
import { EnlargedImage } from './enlarged-image';
import { GenerateAiSuffixButton } from './generate-ai-suffix-button';
import { GenerateWithAiButton } from './generate-with-ai-button';
import { HelperTip } from './helper-tip';
import { Pagination } from './pagination';
export const AltTextScreenContent = _ref => {
  let {
    paginationConfig
  } = _ref;
  const {
    actions,
    state,
    translations,
    biCallbacks,
    wizardPresentation
  } = useSharedAltTextScreenContext();
  const {
    allImages,
    filledCount,
    isGeneratingAi: isGenerating,
    images: stateImages,
    missingCount,
    totalImages: totalCount
  } = state;
  const images = allImages ?? stateImages;
  const generatingImageIds = state.generatingImageIds ?? new Set();
  const showSummary = state.showSummary ?? false;
  const {
    description,
    generateAiLabel,
    progressText,
    helperTip,
    imageItem
  } = translations;
  const {
    onGenerateAi,
    onGenerateSingleImage,
    onRetryFailed,
    onSortUnsupported
  } = actions;
  const showPagination = paginationConfig && paginationConfig.totalPages > 1;
  const elementsWithMissingAlt = useMemo(() => images.filter(imageNeedsAltText), [images]);
  const disabledTooltipKeySuffix = useMemo(() => getDisabledButtonTooltipKeySuffix(elementsWithMissingAlt, isGenerating), [elementsWithMissingAlt, isGenerating]);
  const {
    disabledTooltips
  } = translations;
  const bulkGenerateDisabledTooltip = useMemo(() => {
    if (!disabledTooltipKeySuffix) {
      return undefined;
    }
    return disabledTooltips[disabledTooltipKeySuffix];
  }, [disabledTooltipKeySuffix, disabledTooltips]);
  const isBulkGenerateDisabled = !!disabledTooltipKeySuffix;
  const renderPagination = useCallback(isTopPagination => {
    if (!showPagination || !paginationConfig) {
      return null;
    }
    const {
      currentPage,
      formatItemsRange,
      compactPanelLayout,
      isEditor3,
      onClickBack,
      onClickNext,
      pageSize,
      totalItems,
      totalPages
    } = paginationConfig;
    const useCompactLayout = compactPanelLayout ?? isEditor3 ?? false;
    return /*#__PURE__*/React.createElement(Box, {
      boxSizing: isTopPagination && useCompactLayout ? 'border-box' : undefined,
      direction: "vertical",
      gap: "SP2",
      padding: isTopPagination ? 'SP3 SP4 SP2 SP4' : 'SP3 0 SP2 0',
      width: isTopPagination && !useCompactLayout ? '252px' : undefined
    }, /*#__PURE__*/React.createElement(Pagination, {
      currentPageNumber: currentPage,
      formatItemsRange: formatItemsRange,
      pagesAmount: totalPages,
      pageSize: pageSize,
      totalItems: totalItems,
      onClickBack: () => onClickBack(isTopPagination),
      onClickNext: () => onClickNext(isTopPagination)
    }));
  }, [showPagination, paginationConfig]);
  const getDisabledTooltip = useCallback(image => {
    const keySuffix = getSingleImageDisabledTooltipKeySuffix(image.url);
    if (keySuffix) {
      return disabledTooltips[keySuffix];
    }
    return undefined;
  }, [disabledTooltips]);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    overflow: "hidden"
  }, /*#__PURE__*/React.createElement(AltTextOverviewSection, {
    actionContent: showSummary ? /*#__PURE__*/React.createElement(AltTextSummary, {
      biCallbacks: biCallbacks.summary,
      images: images,
      isGenerating: isGenerating,
      onRetryFailed: onRetryFailed,
      onSortUnsupported: onSortUnsupported
    }) : /*#__PURE__*/React.createElement(GenerateWithAiButton, {
      disabledTooltip: bulkGenerateDisabledTooltip,
      isDisabled: isBulkGenerateDisabled,
      isGenerating: isGenerating,
      label: generateAiLabel,
      missingCount: missingCount,
      onClick: () => {
        biCallbacks.onGenerateClick('bulk');
        onGenerateAi();
      },
      onTooltipShow: () => biCallbacks.onBulkGenerateTooltipView(disabledTooltipKeySuffix)
    }),
    description: description,
    filledCount: filledCount,
    progressText: progressText,
    showDivider: state.showOverviewDivider,
    totalCount: totalCount,
    useStudioSurfaces: wizardPresentation == null ? void 0 : wizardPresentation.softIssueListSurfaces
  }), renderPagination(true), /*#__PURE__*/React.createElement(AltTextImageList, {
    bottomContent: renderPagination(false),
    helperTip: /*#__PURE__*/React.createElement(HelperTip, null, helperTip.content),
    renderInputSuffix: image => {
      const isUnsupportedFormat = image.isAiSupported === false;
      const disabledTooltip = getDisabledTooltip(image);
      const keySuffix = getSingleImageDisabledTooltipKeySuffix(image.url);
      return /*#__PURE__*/React.createElement(GenerateAiSuffixButton, {
        disabledTooltip: disabledTooltip,
        generatingLabel: imageItem.generatingLabel,
        isDisabled: isUnsupportedFormat,
        isGenerating: generatingImageIds.has(image.id),
        label: imageItem.generateLabel,
        onClick: () => {
          biCallbacks.onGenerateClick('single', image.id);
          onGenerateSingleImage(image.id);
        },
        onTooltipShow: () => biCallbacks.element.onTooltipView(keySuffix)
      });
    },
    renderTopContent: (image, isEnlarged, onMinimize) => /*#__PURE__*/React.createElement(EnlargedImage, {
      imageUrl: image.url,
      isEnlarged: isEnlarged,
      onMinimize: onMinimize
    }),
    showSummary: false
  }));
};
//# sourceMappingURL=alt-text-screen-content.js.map